import React from "react";
import {
  Button,
  Modal,
  Form
} from "antd"; // Import Table from antd
import Select from "react-select";
import {
  Control,
  Controller,
  FieldErrors,
  SubmitHandler,
  UseFormHandleSubmit,
} from "react-hook-form";
// utils

import {
  CannyDevice,
  FirmwareBase,
  StringSelectObject,
} from "utils/interfaces";
// components

interface EditCannyModalProps {
    showEditModal: boolean,
    toggleEditModal: () => void,
    loadingButtons: boolean,
    firmwareVersions: FirmwareBase[] | undefined,
    selectedCanny: CannyDevice | null,
    hardwareVersions: StringSelectObject[],
    qcOptions: StringSelectObject[],
    cannyStates: StringSelectObject[],
    updateCanny: SubmitHandler<CannyDevice>,
    cannyEditSubmit: UseFormHandleSubmit<CannyDevice, CannyDevice>,
    control: Control<CannyDevice, any, CannyDevice>,
    errors: FieldErrors<CannyDevice>,
    isValid: boolean,
    isDirty: boolean
}

const EditCannyModal: React.FC<EditCannyModalProps> = ({
    showEditModal,
    toggleEditModal,
    loadingButtons,
    updateCanny,
    cannyEditSubmit,
    firmwareVersions,
    selectedCanny,
    hardwareVersions,
    qcOptions,
    cannyStates,
    control,
    errors,
    isValid,
    isDirty
}) => {

  return (
    <Modal
      title={`Editing access for ${selectedCanny?.serial_no}`}
      open={showEditModal}
      onCancel={toggleEditModal}
      closable={!loadingButtons}
      mask={{ closable: !loadingButtons }}
      footer={[
        <Button onClick={toggleEditModal} disabled={loadingButtons}>Close</Button>,
        <Button
          onClick={cannyEditSubmit(updateCanny)}
          loading={loadingButtons}
          type="primary"
          disabled={!isValid || !isDirty || loadingButtons}
        >Save Changes</Button>
      ]}
    >
      <Form layout="vertical" preserve={false}>
        <Form.Item label="Select Latest Firmware Version">
          <Controller
            name="latest_firmware_id"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                menuPosition="fixed"
                menuPlacement="bottom"
                isSearchable
                options={[
                  { id: null, version_name: "Latest" },
                  ...[...(firmwareVersions || [])].reverse(),
                ]}
                getOptionLabel={(option) => option.version_name}
                getOptionValue={(option) => String(option.id)}
                value={
                  (firmwareVersions ?? []).find((fw) => fw.id === field.value) ||
                  (field.value === null ? { id: null, version_name: "Latest" } : null)
                }
                onChange={(selectedOption) =>
                  field.onChange(selectedOption ? selectedOption.id : null)
                }
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label='Select Hardware Version'
          validateStatus={errors.hardware_version ? "error" : ""}
          help={errors.hardware_version?.message}
        >
          <Controller
            name="hardware_version"
            control={control}
            rules={{ required: "Hardware version is required" }}
            render={({ field }) => (
              <Select
                {...field}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                menuPosition={"fixed"}
                menuPlacement={"bottom"}
                isSearchable
                name="color"
                menuShouldScrollIntoView={false}
                options={hardwareVersions}
                value={hardwareVersions.find(hw => hw.value === field.value) || null}
                onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
              />
            )}
          />
        </Form.Item>
        <Form.Item label='Select Canny QC status'>
          <Controller
            name="qc_pass"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                menuPosition={"fixed"}
                menuPlacement={"bottom"}
                isSearchable
                name="color"
                menuShouldScrollIntoView={false}
                options={qcOptions}
                value={qcOptions.find(qc => qc.value === field.value) || null}
                onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label='Canny State'
          validateStatus={errors.status ? "error" : ""}
          help={errors.status?.message}
        >
          <Controller
            name="status"
            control={control}
            rules={{ required: "Canny state is required" }}
            render={({ field }) => (
              <Select
                {...field}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                menuPosition={"fixed"}
                menuPlacement={"bottom"}
                isSearchable
                name="color"
                menuShouldScrollIntoView={false}
                options={cannyStates}
                value={cannyStates.find((state) => state.value === field.value) || null}
                onChange={(selectedOption) => field.onChange(selectedOption ? selectedOption.value : null)}
              />
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditCannyModal;
